/* ==========================================================================
   ORDESSA — theme behaviour
   Deliberately small. No libraries, no animation frameworks.
   Everything here degrades gracefully: if this file fails to load, the page
   remains fully usable and every control still works.
   ========================================================================== */

(function () {
  'use strict';

  /* ------------------------------------------------------------------
     Sticky mobile add-to-cart
     Mirrors the real product form rather than duplicating it: the button
     submits the actual <form> so variant, quantity and validation stay in
     one place. Appears only once the real buy panel scrolls out of view.
     ------------------------------------------------------------------ */
  class OrdessaStickyAtc extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-ord-sticky-submit]');

      // Watch the real buy panel. Craft renders <product-form> around the
      // add-to-cart form, so we can anchor to it without editing main-product.
      this.watchTarget =
        document.querySelector('[data-ord-atc-anchor]') ||
        document.querySelector('product-form') ||
        document.querySelector('form[action*="/cart/add"]');

      if (!this.button) return;

      this.button.addEventListener('click', this.onClick.bind(this));

      if (!this.watchTarget || !('IntersectionObserver' in window)) {
        // Without an anchor we simply never auto-show; the in-page button
        // remains the single path to purchase.
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            // Show once the real buy button has scrolled above the viewport.
            const belowViewport = entry.boundingClientRect.top > 0;
            this.classList.toggle('is-visible', !entry.isIntersecting && !belowViewport);
          });
        },
        { rootMargin: '0px 0px -120px 0px', threshold: 0 }
      );

      this.observer.observe(this.watchTarget);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    onClick(event) {
      event.preventDefault();

      const form =
        document.querySelector('product-form form[data-type="add-to-cart-form"]') ||
        document.querySelector('form[action*="/cart/add"]');

      if (!form) return;

      const realButton = form.querySelector('[type="submit"]:not([disabled])');

      if (realButton) {
        realButton.click();
      } else {
        // Sold out or unavailable — take the customer to the real control so
        // they can see why, rather than failing silently.
        form.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }

  if (!customElements.get('ordessa-sticky-atc')) {
    customElements.define('ordessa-sticky-atc', OrdessaStickyAtc);
  }

  /* ------------------------------------------------------------------
     Keep the sticky bar's variant label and price in sync with the picker.
     Craft/Dawn publish variant changes on the PUB_SUB_EVENTS.variantChange
     channel via assets/pubsub.js.
     ------------------------------------------------------------------ */
  function bindVariantSync() {
    const bar = document.querySelector('ordessa-sticky-atc');
    if (!bar || typeof subscribe !== 'function') return;

    const metaEl = bar.querySelector('[data-ord-sticky-meta]');
    if (!metaEl) return;

    subscribe('variant-change', (event) => {
      const variant = event && event.data && event.data.variant;
      if (!variant) return;

      const parts = [];
      if (variant.title && variant.title !== 'Default Title') parts.push(variant.title);
      if (metaEl.dataset.moneyFormat && typeof variant.price === 'number') {
        parts.push(formatMoney(variant.price, metaEl.dataset.moneyFormat));
      }
      metaEl.textContent = parts.join(' · ');
    });
  }

  // Minimal money formatter — handles Shopify's {{amount}} style tokens.
  function formatMoney(cents, format) {
    const value = (cents / 100).toFixed(2);
    const withCommas = value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return format
      .replace(/\{\{\s*amount\s*\}\}/, withCommas)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100).toString())
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, value.replace('.', ','));
  }

  /* ------------------------------------------------------------------
     Recently viewed products (privacy-light: handles only, localStorage,
     capped, and never sent anywhere).
     ------------------------------------------------------------------ */
  const RECENT_KEY = 'ordessa:recently-viewed';
  const RECENT_MAX = 8;

  function recordRecentlyViewed() {
    const el = document.querySelector('[data-ord-record-view]');
    if (!el) return;

    const handle = el.dataset.ordRecordView;
    if (!handle) return;

    try {
      const stored = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
      const next = [handle].concat(stored.filter((h) => h !== handle)).slice(0, RECENT_MAX);
      localStorage.setItem(RECENT_KEY, JSON.stringify(next));
    } catch (e) {
      /* Storage unavailable (private mode, quota) — feature simply skipped. */
    }
  }

  /* ------------------------------------------------------------------
     Announce cart updates to assistive technology.
     Craft updates the cart via fetch; without a live region, screen reader
     users get no confirmation that anything happened.
     ------------------------------------------------------------------ */
  function bindCartAnnouncements() {
    const region = document.getElementById('ordessa-cart-live-region');
    if (!region || typeof subscribe !== 'function') return;

    subscribe('cart-update', () => {
      region.textContent = region.dataset.messageUpdated || 'Cart updated.';
      window.setTimeout(() => { region.textContent = ''; }, 3000);
    });
  }

  function init() {
    bindVariantSync();
    recordRecentlyViewed();
    bindCartAnnouncements();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
